import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { equals, not, spread } from 'patronum';
import {
	ACCOUNT_NAME_MIN_LENGTH,
	ACCOUNT_NOTE_MAX_LENGTH,
	accountTreeModel,
} from 'entities/account';
import { viewerModel } from 'entities/viewer';
import {
	AccountEditResponse,
	AccountWithWalletsAndPermissions,
	createEditAccountMutation,
	EditAccountMutationParams,
} from 'shared/api';
import {
	createModalFormModel,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AccountEditFormValue = {
	name: string;
	note: string;
	canCreateSubAccounts: boolean;
	canWriteUsers: boolean;
	canEditLimits: boolean;
	canEditGameSettings: boolean;
	canWriteCashdesks: boolean;
	canBlockGames: boolean;
};

export type EditAccountModel = ModalFormModel<
	AccountWithWalletsAndPermissions,
	AccountEditFormValue
> & {
	editAccountMutation: Mutation<
		EditAccountMutationParams,
		AccountEditResponse,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createEditAccountModel = (): EditAccountModel => {
	const editAccountMutation = createEditAccountMutation();

	const model = createModalFormModel<AccountWithWalletsAndPermissions, AccountEditFormValue>({
		fields: {
			name: {
				init: '',
				rules: [notEmptyRule, minLengthRule(ACCOUNT_NAME_MIN_LENGTH)],
			},
			note: {
				init: '',
				rules: [maxLengthRule(ACCOUNT_NOTE_MAX_LENGTH)],
			},
			canCreateSubAccounts: {
				init: false,
			},
			canWriteUsers: {
				init: false,
			},
			canEditLimits: {
				init: false,
			},
			canEditGameSettings: {
				init: false,
			},
			canWriteCashdesks: {
				init: false,
			},
			canBlockGames: {
				init: false,
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = editAccountMutation;

	const $isAttachedAccount = equals(
		viewerModel.$accountId,
		model.$modalData.map((account) => account?.id || -1),
	);

	sample({
		clock: model.modalButtonClicked,
		source: viewerModel.$permissions,
		filter: not($isAttachedAccount),
		target: spread({
			canCreateSubAccounts: model.form.fields.canCreateSubAccounts.setActive,
			canWriteUsers: model.form.fields.canWriteUsers.setActive,
			canEditLimits: model.form.fields.canEditLimits.setActive,
			canEditGameSettings: model.form.fields.canEditGameSettings.setActive,
			canWriteCashdesks: model.form.fields.canWriteCashdesks.setActive,
			canBlockGames: model.form.fields.canBlockGames.setActive,
		}),
	});

	sample({
		clock: model.modalButtonClicked,
		filter: $isAttachedAccount,
		target: [
			model.form.fields.canCreateSubAccounts.disable,
			model.form.fields.canWriteUsers.disable,
			model.form.fields.canEditLimits.disable,
			model.form.fields.canEditGameSettings.disable,
			model.form.fields.canWriteCashdesks.disable,
			model.form.fields.canBlockGames.disable,
		],
	});

	sample({
		clock: model.modalButtonClicked,
		fn: ({ note, permissions, ...account }) => ({
			note: note ?? '',
			...account,
			...permissions,
		}),
		target: [model.form.setValues, model.form.setInitialValues],
	});

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			id: model.$modalData.map((account) => account?.id ?? null),
		},
		filter: ({ id }) => id !== null,
		fn: ({ values: { name, note, ...permissions }, id }) => ({
			id: id!,
			note,
			name,
			permissions: {
				...permissions,
			},
		}),
		target: editAccountMutation.start,
	});

	sample({
		clock: editAccountMutation.finished.success,
		target: [model.switcher.disable, accountTreeModel.fetch],
	});

	notify(editAccountMutation.finished.success, 'edit-account.success');

	chain(model.modalClosed, editAccountMutation.reset);

	return {
		editAccountMutation,
		$pending,
		...model,
	};
};
